// src/resources/POSMappings/POSMappingList.js

import React, { useEffect, useState } from "react";
import {
  List,
  Datagrid,
  TextField,
  useRecordContext,
  EditButton,
  TopToolbar,
  CreateButton,
} from "react-admin";

import * as XLSX from "xlsx";
import { db } from "../../firebase/firebase";

import {
  collection,
  setDoc,
  doc,
  getDocs,
  getDoc,
} from "firebase/firestore";

import { Pagination } from "react-admin";

/* ------------------------------------------------------------
   COMPANY NORMALIZER
------------------------------------------------------------- */
function normalizeCompanyName(str = "") {
  const txt = str.trim().toLowerCase();
  if (!txt) return "";

  if (txt.includes("comfort"))
    return "Comfort Line Luxury Motor Vehicles Services LLC";

  if (txt.includes("lolia"))
    return "Lolia Limousine for Luxury Motor Vehicles Services LLC";

  return "Comfort Line Luxury Motor Vehicles Services LLC";
}

/* ------------------------------------------------------------
   Excel Serial Date → ISO
------------------------------------------------------------- */
function excelSerialToISO(v) {
  if (!v) return null;

  if (typeof v === "number") {
    const excelEpoch = new Date(Date.UTC(1899, 11, 30));
    const date = new Date(excelEpoch.getTime() + v * 86400000);
    return date.toISOString().slice(0, 10);
  }

  const d = new Date(v);
  if (!isNaN(d)) return d.toISOString().slice(0, 10);

  return null;
}

/* ------------------------------------------------------------
   Pagination
------------------------------------------------------------- */
const BigPagination = (props) => (
  <Pagination rowsPerPageOptions={[5, 10, 25, 50, 100]} {...props} />
);

/* ------------------------------------------------------------
   VEHICLE DISPLAY
   Resolve the CURRENT assignment from posDeviceAssignments and then
   resolve its vehicle number from a single in-memory vehicles map.

   This keeps posDeviceAssignments/currentVehicleId as the source of
   truth while avoiding one React-Admin ReferenceField query per row.
------------------------------------------------------------- */
const VehicleNumberField = ({ vehicleMap, currentAssignmentMap }) => {
  const record = useRecordContext();
  if (!record) return null;

  const terminalKey = String(record.terminalId || record.id || "").trim();
  const hasAssignmentState = Object.prototype.hasOwnProperty.call(
    currentAssignmentMap,
    terminalKey
  );

  // If an assignment document exists, trust its current assignment state.
  // Otherwise fall back to the legacy mapping's vehicleId.
  const currentVehicleId = hasAssignmentState
    ? currentAssignmentMap[terminalKey]
    : record.vehicleId || "";

  if (!currentVehicleId) {
    return <span>UNASSIGNED</span>;
  }

  const vehicleNumber = vehicleMap[currentVehicleId];

  // record.vehicleNumber is only a last-resort fallback for legacy records.
  // It is intentionally NOT preferred because that cached field may be stale.
  const fallbackNumber =
    currentVehicleId === record.vehicleId ? record.vehicleNumber : "";

  return (
    <span>{vehicleNumber || fallbackNumber || currentVehicleId}</span>
  );
};

/* ------------------------------------------------------------
   ADD ASSIGNMENT ENTRY (MULTIPLE ALLOWED)
------------------------------------------------------------- */
async function addAssignmentEntry(
  terminalId,
  vehicleId,
  startDateRaw,
  endDateRaw,
  deviceCompany
) {
  const startDate = excelSerialToISO(startDateRaw) || "2025-11-01";
  const endDate = endDateRaw ? excelSerialToISO(endDateRaw) : null;

  const assignmentRef = doc(db, "posDeviceAssignments", terminalId);

  const snap = await getDoc(assignmentRef);

  let oldAssignments = [];
  if (snap.exists()) {
    oldAssignments = snap.data().assignments || [];
  }

  const newEntry = {
    vehicleId,
    startDate,
    endDate,
    deviceCompany,
    createdAt: new Date(),
  };

  const updatedAssignments = [...oldAssignments, newEntry];

  await setDoc(
    assignmentRef,
    {
      terminalId,
      deviceCompany,
      assignments: updatedAssignments,
      updatedAt: new Date(),
      currentVehicleId: endDate ? null : vehicleId,
    },
    { merge: true }
  );
}

/* ------------------------------------------------------------
   UPLOAD COMPONENT
------------------------------------------------------------- */
function POSMappingUploadBox() {
  const [uploading, setUploading] = useState(false);

  async function handleExcelUpload(e) {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);

    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf);
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet);

      const vehiclesSnap = await getDocs(collection(db, "vehicles"));
      const vehicles = vehiclesSnap.docs.map((d) => ({
        id: d.id,
        number: String(d.data().vehicleNumber || "").trim(),
        data: d.data(),
      }));

      for (const row of rows) {
        const rawVehicle =
          row.vehicleId ||
          row.vehicleNumber ||
          row["Vehicle No"] ||
          row["Vehicle"] ||
          row["Vehicle Number"] ||
          "";

        const vehicleNumberNum = rawVehicle.match(/\d+/)?.[0] || null;

        const terminalId = String(
          row.terminalId ||
          row["Terminal ID"] ||
          row["TerminalId"] ||
          row["Terminal"] ||
          row["POS Terminal"] ||
          ""
        ).trim();

        const rawCompany =
          row.deviceCompany ||
          row.DeviceCompany ||
          row["Device Company"] ||
          row.company ||
          row.Company ||
          row.__EMPTY ||
          row.__EMPTY_1 ||
          "";

        const deviceCompany = normalizeCompanyName(rawCompany);

        const startDateRaw =
          row.startDate ||
          row["Start Date"] ||
          row.Start ||
          row["From"] ||
          null;

        const endDateRaw =
          row.endDate ||
          row["End Date"] ||
          row.End ||
          row["To"] ||
          null;

        if (!vehicleNumberNum || !terminalId) {
          continue;
        }

        // MATCH VEHICLE
        const matchedVehicle = vehicles.find((v) =>
          v.number.startsWith(vehicleNumberNum)
        );

        if (!matchedVehicle) {
          continue;
        }

        const vehicleId = matchedVehicle.id;
        const vehicleNumber = matchedVehicle.data.vehicleNumber;

        // SAVE MAPPING
        await setDoc(
          doc(db, "posDeviceMappings", terminalId),
          {
            terminalId,
            vehicleId,
            vehicleNumber,
            deviceCompany,
            updatedAt: new Date().toISOString(),
          },
          { merge: true }
        );

        // SAVE ASSIGNMENT
        await addAssignmentEntry(
          terminalId,
          vehicleId,
          startDateRaw,
          endDateRaw,
          deviceCompany
        );
      }

      alert("POS mappings uploaded successfully!");
    } catch (err) {
      console.error(err);
      alert("Failed to upload mappings!");
    }

    setUploading(false);
  }

  return (
    <div style={{ marginBottom: 20 }}>
      <label
        style={{
          background: "#0037ff",
          color: "white",
          padding: "8px 14px",
          borderRadius: "6px",
          cursor: "pointer",
          fontSize: 14,
          display: "inline-block",
        }}
      >
        📥 Upload POS Excel
        <input
          type="file"
          accept=".xlsx"
          style={{ display: "none" }}
          onChange={handleExcelUpload}
          disabled={uploading}
        />
      </label>

      {uploading && <span style={{ marginLeft: 10 }}>Uploading…</span>}
    </div>
  );
}

/* ------------------------------------------------------------
   MAIN LIST PAGE
------------------------------------------------------------- */
export const POSMappingList = (props) => {
  const memoPagination = React.useMemo(() => <BigPagination />, []);
  const [vehicleMap, setVehicleMap] = useState({});
  const [currentAssignmentMap, setCurrentAssignmentMap] = useState({});

  useEffect(() => {
    let mounted = true;

    async function loadCurrentAssignments() {
      try {
        // Two collection reads total for the whole page:
        // 1) vehicles -> vehicleId => vehicleNumber
        // 2) POS assignment docs -> terminalId => currentVehicleId
        const [vehiclesSnap, assignmentsSnap] = await Promise.all([
          getDocs(collection(db, "vehicles")),
          getDocs(collection(db, "posDeviceAssignments")),
        ]);

        if (!mounted) return;

        const nextVehicleMap = {};
        vehiclesSnap.forEach((vehicleDoc) => {
          const data = vehicleDoc.data() || {};
          nextVehicleMap[vehicleDoc.id] =
            data.vehicleNumber || data.number || vehicleDoc.id;
        });

        const nextAssignmentMap = {};
        assignmentsSnap.forEach((assignmentDoc) => {
          const data = assignmentDoc.data() || {};
          const terminalKey = String(
            data.terminalId || assignmentDoc.id || ""
          ).trim();
          if (!terminalKey) return;

          let currentVehicleId = "";

          // The assignment array is the same history used by POS allocation.
          // Derive the current vehicle from its open assignment first.
          if (Array.isArray(data.assignments)) {
            const openAssignments = data.assignments.filter(
              (assignment) => assignment?.vehicleId && !assignment?.endDate
            );
            const latestOpen = openAssignments[openAssignments.length - 1];
            currentVehicleId = String(latestOpen?.vehicleId || "").trim();
          } else {
            // Backwards compatibility for very old assignment documents.
            currentVehicleId = String(data.currentVehicleId || "").trim();
          }

          // Keeping an explicit empty value is important: it tells the list
          // that this terminal has assignment history but is currently free.
          nextAssignmentMap[terminalKey] = currentVehicleId;
        });

        setVehicleMap(nextVehicleMap);
        setCurrentAssignmentMap(nextAssignmentMap);
      } catch (error) {
        console.error("Failed to load current POS assignments:", error);
      }
    }

    loadCurrentAssignments();

    return () => {
      mounted = false;
    };
  }, []);

  return (
    <>
      <POSMappingUploadBox />

      <List
        {...props}
        perPage={100}
        sort={{ field: "terminalId", order: "ASC" }}
        title="POS Device Mappings"
        actions={<TopToolbar><CreateButton /></TopToolbar>}
        pagination={memoPagination}
        disableSyncWithLocation
      >
        <Datagrid rowClick="edit">
          <TextField source="terminalId" label="Terminal ID" />

          <VehicleNumberField
            label="Vehicle"
            vehicleMap={vehicleMap}
            currentAssignmentMap={currentAssignmentMap}
          />

          <TextField source="deviceCompany" label="Device Company" />
          <EditButton />
        </Datagrid>
      </List>
    </>
  );
};
